import { STORY_TEMPLATES, StoryTemplate } from "@/lib/storyTemplates.ts";
import { Chapter, PlotCard } from '@/pages/timeline/types';
import { supabase } from './supabase/supabase';

/**
 * Result object returned when applying a template to a timeline
 */
export interface TemplateApplicationResult {
  chapters: Chapter[];
  cards: PlotCard[];
  success: boolean; // Changed from optional to required
  error?: string;
}

// No helper functions needed anymore as we're using the Supabase function directly

/**
 * Apply a template to an existing timeline using the Supabase function
 */
export const applyTemplateToExistingTimeline = async (
  templateId: string,
  timelineId: number
): Promise<TemplateApplicationResult> => {
  try {
    console.log(`Starting to apply template ${templateId} to timeline ${timelineId}`);

    // Get the story ID from the timeline
    const { data: timeline, error: timelineError } = await supabase
      .from('timelines')
      .select('story_id')
      .eq('id', timelineId)
      .single();

    if (timelineError) {
      throw new Error(`Could not find timeline with ID ${timelineId}: ${timelineError.message}`);
    }

    if (!timeline || !timeline.story_id) {
      throw new Error(`Timeline with ID ${timelineId} has no associated story`);
    }

    const storyId = timeline.story_id;
    console.log(`Found story ID ${storyId} for timeline ${timelineId}`);

    // Call the Supabase function directly
    const { data, error } = await supabase.rpc('apply_template_to_story', {
      p_story_id: storyId,
      p_template_id: templateId,
      p_timeline_id: timelineId
    });

    if (error) {
      throw new Error(`Error applying template: ${error.message}`);
    }

    console.log('Template application result:', data);

    // Transform the result to match the expected TemplateApplicationResult format
    return {
      success: data.success,
      chapters: data.chapters || [],
      cards: data.cards || [],
      error: data.error
    };
  } catch (error) {
    console.error(`Error applying template ${templateId} to timeline ${timelineId}:`, error);
    throw error;
  }
};

// Define Timeline interface based on what's being returned from createTimeline
interface Timeline {
  id: number;
  name?: string;
  title?: string;
  description?: string;
  story_id?: string;
  [key: string]: any;
}

// Define Template types
export interface TemplateBeat {
  title: string;
  content: string;
  chapter: number;
  type?: string;
  suggestedChapterTitle?: string;
  chapterPosition?: number;
  description?: string;
}

export interface Template {
  id: string;
  name: string;
  description: string;
  recommendedChapters: number;
  beats: TemplateBeat[];
}

/**
 * Get template data
 */
export const getTemplateData = async (id: string): Promise<StoryTemplate> => {
  // For now, return the in-memory templates
  // This could be replaced with a database or API fetch in the future
  const template = STORY_TEMPLATES[id];
  if (!template) {
    throw new Error(`Template with ID ${id} not found`);
  }
  return template;
};

/**
 * Create a new timeline from a template
 */
export const createTimelineFromTemplate = async (
  templateId: string,
  title: string,
  description?: string,
  storyId?: string
): Promise<{ success: boolean; timeline: Timeline | null; error?: string }> => {
  try {
    console.log(`Creating new timeline "${title}" from template ${templateId} with storyId ${storyId}`);

    // 1. Create the timeline
    let timeline: Timeline | null = null;
    try {
      // Create the timeline first
      const { data, error } = await supabase
        .from('timelines')
        .insert({
          title,
          description: description || "",
          story_id: storyId || null,
          is_primary: true
        })
        .select()
        .single();

      if (error) {
        throw error;
      }

      timeline = data as Timeline;

      if (!timeline || !timeline.id) {
        throw new Error("Failed to create timeline");
      }

      console.log(`Created timeline with ID: ${timeline.id}`);
    } catch (error) {
      console.error("Timeline creation error:", error);
      return {
        success: false,
        timeline: null,
        error: `Failed to create timeline: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }

    // If this is an empty template, just return the timeline
    if (templateId === "empty") {
      return {
        success: true,
        timeline: timeline
      };
    }

    // 2. Apply template to the timeline using the Supabase function
    try {
      if (!storyId) {
        throw new Error("Story ID is required to apply a template");
      }

      const { data, error } = await supabase.rpc('apply_template_to_story', {
        p_story_id: parseInt(storyId),
        p_template_id: templateId,
        p_timeline_id: timeline.id
      });

      if (error) {
        throw new Error(`Error applying template: ${error.message}`);
      }

      console.log('Template application result:', data);

      if (!data.success) {
        return {
          success: false, // Return false if template application failed
          timeline: timeline,
          error: `Timeline created but template application failed: ${data.error || 'Unknown error'}`
        };
      }
    } catch (templateError) {
      console.error(`Error applying template, but timeline was created:`, templateError);
      return {
        success: true, // Still return success since timeline was created
        timeline: timeline,
        error: `Timeline created but template application failed: ${templateError instanceof Error ? templateError.message : 'Unknown error'}`
      };
    }

    console.log(`Successfully created timeline "${title}" with ID ${timeline.id}`);
    // 3. Return the timeline
    return {
      success: true,
      timeline: timeline
    };
  } catch (error) {
    console.error(`Error creating timeline "${title}" from template "${templateId}":`, error);
    return {
      success: false,
      timeline: null,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

/**
 * Create a new timeline with a specific number of empty chapters
 */
export const createTimelineWithChapters = async (
  storyId: string,
  title: string,
  chapterCount: number
): Promise<{ success: boolean; timeline: Timeline | null; error?: string }> => {
  try {
    console.log(`Creating timeline "${title}" with ${chapterCount} chapters for story ${storyId}`);

    // 1. Create the timeline
    const { data: timeline, error: timelineError } = await supabase
      .from('timelines')
      .insert({
        title,
        description: "",
        story_id: storyId,
        is_primary: true
      })
      .select()
      .single();

    if (timelineError) {
      throw new Error(`Failed to create timeline: ${timelineError.message}`);
    }

    if (!timeline) {
      throw new Error("Failed to create timeline: No data returned");
    }

    console.log(`Created timeline with ID: ${timeline.id}`);

    // 2. Create chapters
    // We'll import createChapter dynamically or use supabase directly to avoid circular dependencies if any
    // For now, let's use supabase direct insertion for batch performance or just loop
    try {
        const chaptersToCreate = Array.from({ length: chapterCount }, (_, i) => ({
            story_id: parseInt(storyId),
            title: `Chapter ${i + 1}`,
            description: "",
            // tenant_id handled by RLS
        }));

        // Option A: Use createChapter from chapters service (cleaner but sequential)
        // Option B: Batch insert (faster)
        
        // Let's use batch insert for efficiency
        const { error: chaptersError } = await supabase
            .from('chapters')
            .insert(chaptersToCreate);

        if (chaptersError) {
            console.error("Error creating chapters:", chaptersError);
            // We don't rollback timeline creation, but we report the partial error?
            // Or we treat it as success since timeline is there, just chapters failed.
             throw new Error(`Timeline created but chapters failed: ${chaptersError.message}`);
        }

        console.log(`Successfully created ${chapterCount} chapters`);

    } catch (chapterError) {
         console.error("Chapter creation error:", chapterError);
         return {
            success: true, // Timeline was created, so partial success
            timeline: timeline as Timeline,
            error: `Timeline created but chapter generation failed: ${chapterError instanceof Error ? chapterError.message : 'Unknown error'}`
         };
    }

    return {
      success: true,
      timeline: timeline as Timeline
    };

  } catch (error) {
    console.error("Error in createTimelineWithChapters:", error);
    return {
      success: false,
      timeline: null,
      error: error instanceof Error ? error.message : "Unknown error"
    };
  }
};

// Export template service with only the necessary functions
export const templateService = {
  applyTemplateToExistingTimeline,
  createTimelineFromTemplate,
  createTimelineWithChapters,
  getTemplateData
};